import React, { useState } from 'react';
import { useCreateOneTimerTask } from '../api/oneTimersApi';
import { Field, Input, Label, Textarea, Description } from '@headlessui/react';
import clsx from 'clsx';

interface Props {
  onSuccess?: () => void;
}

const AddOneTimerForm: React.FC<Props> = ({ onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const createTaskMutation = useCreateOneTimerTask();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      await createTaskMutation.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        deadline: deadline || undefined,
      });
      setTitle('');
      setDescription('');
      setDeadline('');
      setError(null);
      setTouched(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  };

  const handleTitleBlur = () => {
    setTouched(true);
    if (!title.trim()) {
      setError('Task title is required');
    } else {
      setError(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Title Field */}
      <Field>
        <Label className="text-sm font-medium text-gray-700">Task Title *</Label>
        <Input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleTitleBlur}
          className={clsx(
            'mt-1 block w-full rounded-md px-3 py-2 text-gray-900',
            'border shadow-sm transition-colors',
            'focus:outline-none focus:ring-2',
            error && touched
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20',
            'disabled:bg-gray-50 disabled:text-gray-500'
          )}
          placeholder="Buy groceries"
          disabled={createTaskMutation.isPending}
        />
        {error && touched && (
          <Description className="mt-1 text-sm text-red-600">{error}</Description>
        )}
      </Field>

      {/* Description Field */}
      <Field>
        <Label className="text-sm font-medium text-gray-700">Description</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className={clsx(
            'mt-1 block w-full rounded-md px-3 py-2 text-gray-900',
            'border border-gray-300 shadow-sm',
            'focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500/20',
            'disabled:bg-gray-50 disabled:text-gray-500',
            'resize-none'
          )}
          placeholder="Optional details..."
          disabled={createTaskMutation.isPending}
        />
      </Field>

      {/* Deadline Field */}
      <Field>
        <Label className="text-sm font-medium text-gray-700">Deadline</Label>
        <Input
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          className={clsx(
            'mt-1 block w-full rounded-md px-3 py-2 text-gray-900',
            'border border-gray-300 shadow-sm',
            'focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500/20',
            'disabled:bg-gray-50 disabled:text-gray-500'
          )}
          disabled={createTaskMutation.isPending}
        />
      </Field>

      <button
        type="submit"
        disabled={createTaskMutation.isPending || !title.trim()}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {createTaskMutation.isPending ? 'Adding...' : 'Add Task'}
      </button>
    </form>
  );
};

export default AddOneTimerForm; 